import React, { useState, useMemo, useEffect } from "react";
import PaymentModal from "./PaymentModal";
import EmployeeSalaryTable from "./EmployeeSalaryTable";
import { generateMonthsFrom2025, useIsMobile } from "../../utilities";
import { FaCheckCircle, FaDownload } from "react-icons/fa";
import Select from "react-select";
import { formatToIndianNumber } from "../../utilities";
import { FaUserGroup } from "react-icons/fa6";
import { SiVirustotal } from "react-icons/si";
import { IoIosWarning } from "react-icons/io";
import {
  checkPayrollAvailbaleApi,
  createPayrollApi,
  getPayrollExportApi,
  getPayrollListApi,
} from "../../services/salaryService";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import { Spinner } from "react-bootstrap";
import { BiSolidFileExport } from "react-icons/bi";
import { toast } from "react-toastify";
import Pagination from "../Constant/Pagination";
import { CancelRequest } from "../../services/AxiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../Images/dcwlogo.png";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { Dropdown } from "react-bootstrap";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getServiceCenterOptionApi } from "../../services/SearchService";

const generateCombinedPDF = (employees) => {
  const doc = new jsPDF();

  employees.forEach((employee, index) => {
    if (index > 0) doc.addPage(); // Add new page for every employee except the first

    // --- HEADER (Redesigned) ---
    doc.addImage(logo, "PNG", 15, 10, 25, 25);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 0, 0);

    // Service center name (single line, but right aligned)
    doc.text(employee?.service_center_name || "-", 200 - 8, 18, {
      align: "right",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);

    // Define max width for right-side text (avoid overlapping logo)
    const maxRightWidth = 120; // 200 - 15 (right margin) - 65 (space for logo)

    // Wrap service center address
    const address = doc.splitTextToSize(
      employee?.service_center_address || "-",
      maxRightWidth
    );
    doc.text(address, 200 - 8, 26, { align: "right" });

    // Wrap contact info
    doc.setTextColor(0, 0, 0);
    const contact = doc.splitTextToSize(
      `${employee?.service_center_contact_email || "-"} | ${
        employee?.service_center_contact_number || "-"
      }`,
      maxRightWidth
    );
    doc.text(contact, 200 - 8, 31 + (address.length - 1) * 5, {
      align: "right",
    });

    // Divider
    doc.setDrawColor(180);
    doc.line(15, 40, 195, 40);

    // --- Salary Slip Title ---
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Salary Slip", 105, 50, { align: "center" });

    // --- Employee Info (Left & Right) ---
    let y = 60;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Employee Details", 15, y);

    y += 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    // Left Column
    doc.text(`Name: ${employee.employee_name}`, 15, y);
    doc.text(`Email: ${employee.employee_email}`, 15, y + 7);
    doc.text(`Total Days: ${employee.total_days} days`, 15, y + 14);
    doc.text(`Present Days: ${employee.present_days} days`, 15, y + 21);
    doc.text(`Leave Days: ${employee.leave_days} days`, 15, y + 28);
    doc.text(`Absent Days: ${employee.absent_days} days`, 15, y + 35);

    // Right Column
    doc.text(`Month: ${employee.month}-${employee.year}`, 120, y);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 120, y + 7);

    // --- Salary Breakdown Table ---

    autoTable(doc, {
      startY: y + 45,
      theme: "grid",
      head: [["Component", "Amount (Rs.)"]], // use Rs. instead of ₹
      body: [
        ["Base Salary", `Rs. ${employee.base_salary}`],
        ["Overtime", `Rs. ${employee.overtime}`],
        ["Deductions", `Rs. ${employee.deductions}`],
        ["Advances", `Rs. ${employee.advances}`],
        [
          {
            content: "Net Salary",
            styles: {
              halign: "right",
              fontStyle: "bold",
              fillColor: [255, 0, 0],
              textColor: 255,
            },
          },
          `Rs. ${employee.net_salary}`,
        ],
      ],
      styles: { fontSize: 10, font: "helvetica" },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    });

    // --- Payment Status + Signature ---
    let finalY = doc.lastAutoTable.finalY + 25;
    doc.setFontSize(10);

    const leftX = 15;
    const valueRightX = 85; // right-aligned values

    doc.setFont("helvetica", "bold");
    doc.text("Payment Status:", leftX, finalY);
    doc.setFont("helvetica", "normal");
    doc.text(`${employee.payment_status}`, valueRightX, finalY, {
      align: "right",
    });

    doc.setFont("helvetica", "bold");
    doc.text("Paid At:", leftX, finalY + 7);
    doc.setFont("helvetica", "normal");
    doc.text(`${employee.paid_at}`, valueRightX, finalY + 7, {
      align: "right",
    });

    // Right side - Signature
    const sigX = 150;
    const sigWidth = 50;
    const sigY = finalY + 5;

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(sigX, sigY, sigX + sigWidth, sigY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text("Authorized Signature", sigX + sigWidth / 2, sigY + 6, {
      align: "center",
    });

    // --- Footer ---
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(
      "This is a system-generated salary slip. Thank you for your service.",
      105,
      285,
      { align: "center" }
    );
  });

  // Save combined PDF
  doc.save(`Employees_SalarySlips.pdf`);
};

export default function SalaryDashboard() {
  const isMobile = useIsMobile();
  const [exportLoading, setExportLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [isPayrollCreated, setIsPayrollCreated] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [careatePayrollLoading, setCreatePayrollLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    generateMonthsFrom2025()[0]
  );
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [param, setParam] = useState({
    page: "1",
    page_size: "50",
    query: "",
    service_center: "",
    year: String(new Date().getFullYear()),
    month: String(new Date().getMonth() + 1),
  });

  const handleMonthChange = (option) => {
    setSelectedMonth(option);
    setSelectedEmployees([]);
    setSelectedEmployee(null);
    const [monthName, yearStr] = option.value.split(" ");
    const year = parseInt(yearStr, 10);
    const selectedMonthIndex = new Date(
      `${monthName} 1, ${yearStr}`
    ).getMonth();
    setParam((prev) => ({
      ...prev,
      year: String(year),
      month: String(selectedMonthIndex + 1),
    }));
  };

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isBulkPaymentModalOpen, setIsBulkPaymentModalOpen] = useState(false);

  //service-center
  const [servicecenterOption, setServiceCenterOption] = useState([]);
  const [selectedServiceCenter, setSelectedServiceCenter] = useState(null);

  const getServiceCenter = () => {
    getServiceCenterOptionApi()
      .then((res) => {
        setServiceCenterOption(res?.data?.results || []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      });
  };
  // Toggle  for bulk
  const toggleEmployeeSelection = (employee) => {
    setSelectedEmployees((prev) =>
      prev.some((item) => item?.id == employee?.id)
        ? prev.filter((emp) => emp?.id !== employee?.id)
        : [...prev, employee]
    );
  };

  // Single payment
  const handlePayEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setIsPaymentModalOpen(false);
    setSelectedEmployee(null);
    setIsBulkPaymentModalOpen(false);
    setSelectedEmployees([]);
    getPayoll();
  };

  // Bulk payment
  const handleBulkPayment = () => {
    if (selectedEmployees.length > 0) {
      setIsBulkPaymentModalOpen(true);
    }
  };

  // Select all pending
  const handleSelectAllEmployees = (isSelected) => {
    if (isSelected) {
      const pendingEmployee = data.filter(
        (emp) => emp?.payment_status?.toLowerCase() === "pending"
      );
      setSelectedEmployees(pendingEmployee);
    } else {
      setSelectedEmployees([]);
    }
  };

  //get payroll data
  const getPayoll = () => {
    setLoading(true);
    getPayrollListApi(param)
      .then((res) => {
        setData(res?.data?.results || []);
        setTotal(res?.data?.count || 0);
        setSummary(res?.data?.summary || null);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getPayoll();
  }, [param]);

  useEffect(() => {
    getServiceCenter();
  }, []);

  const handlePageChange = (page) => {
    setParam((prev) => ({
      ...prev,
      page: page.selected + 1,
    }));
  };
  const handleSearchChange = (value) => {
    if (value.length > 0) CancelRequest();
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  //check payroll available or not
  const checkPayroll = (data) => {
    setCheckLoading(true);
    checkPayrollAvailbaleApi(data)
      .then((res) => {
        setIsPayrollCreated(res?.data?.payroll_created);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setCheckLoading(false);
      });
  };

  useEffect(() => {
    const value = selectedServiceCenter?.value || "";
    if (param?.service_center !== value) {
      setParam({
        ...param,
        service_center: selectedServiceCenter?.value || "",
      });
    }
  }, [selectedServiceCenter]);
  useEffect(() => {
    const [monthName, yearStr] = selectedMonth.value.split(" ");
    const year = parseInt(yearStr, 10);
    const selectedMonthIndex = new Date(
      `${monthName} 1, ${yearStr}`
    ).getMonth();

    checkPayroll({
      year: String(year),
      month: String(selectedMonthIndex + 1),
    });
  }, [selectedMonth]);

  //payroll-create
  const handleCreatePayroll = () => {
    setCreatePayrollLoading(true);
    const [monthName, yearStr] = selectedMonth.value.split(" ");
    const year = parseInt(yearStr, 10);
    const selectedMonthIndex = new Date(
      `${monthName} 1, ${yearStr}`
    ).getMonth();
    createPayrollApi({
      year: String(year),
      month: String(selectedMonthIndex + 1),
    })
      .then((res) => {
        toast.success("Payroll created successfully");
        setIsPayrollCreated(true);
        getPayoll();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => setCreatePayrollLoading(false));
  };

  const handleExport = () => {
    setExportLoading(true);
    getPayrollExportApi({ ...param, export: true })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "Payroll-report.xlsx";

        // Append to DOM to make it more consistent
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setExportLoading(false);
      });
  };
  return (
    <>
      {/* Header */}
      <div className="card mb-3">
        <div className="d-flex justify-content-between align-items-start gap-2 flex-start p-3 flex-wrap">
          <div className="d-flex flex-column">
            <h5 className="mb-0">Salary Management</h5>
            <span
              className="text-dark fw-medium"
              style={{ fontSize: "0.8rem" }}
            >
              Manage your payments
            </span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <div>
              <Select
                placeholder="Select Month"
                classNamePrefix="select"
                className="react-select"
                options={generateMonthsFrom2025()}
                onChange={handleMonthChange}
                value={selectedMonth}
              />
            </div>

            <Dropdown>
              <Dropdown.Toggle
                variant="success"
                id="dropdown-basic"
                disabled={exportLoading || data?.length == 0}
              >
                {exportLoading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <FaDownload className="me-1" />
                )}
                Export
              </Dropdown.Toggle>

              <Dropdown.Menu
                align="end"
                className="shadow-sm mt-1"
                style={{
                  zIndex: 1050,
                  minWidth: "120px",
                }}
              >
                <Dropdown.Item onClick={() => generateCombinedPDF(data)}>
                  <BsFileEarmarkPdfFill className="me-1" />
                  PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExport}>
                  <PiMicrosoftExcelLogoFill className="me-1" />
                  Excel
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div className="row g-3 py-2 px-3">
          <div className="col-6 col-lg-3">
            <div
              className="card shadow-sm rounded-4 h-100 overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #ffffff, #f1f5f9)",
                position: "relative",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 20px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-15%",
                  right: "-15%",
                  width: "140px",
                  height: "140px",
                  background:
                    "linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(254, 202, 202, 0.1))",
                  borderRadius: "50%",
                  filter: "blur(50px)",
                  opacity: "0.7",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: "-20%",
                  left: "-20%",
                  width: "120px",
                  height: "120px",
                  background:
                    "linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(254, 202, 202, 0.08))",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  opacity: "0.5",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "4px",
                  background: "linear-gradient(90deg, #ef4444, #f87171)",
                  borderRadius: "4px 4px 0 0",
                }}
              ></div>
              <div
                className={`card-body d-flex justify-content-between align-items-center ${
                  isMobile ? "p-3" : "p-4"
                }`}
              >
                <div>
                  <h5
                    className="fw-bold m-0 text-primary"
                    style={{ fontSize: "1.2rem" }}
                  >
                    {summary?.total_employees || 0}
                  </h5>
                  <span className="fw-medium" style={{ fontSize: "0.85rem" }}>
                    Total Employees
                  </span>
                </div>
                <span
                  className="d-none rounded-3 d-sm-flex justify-content-center align-items-center"
                  style={{
                    padding: "0.7rem",
                    background: "linear-gradient(135deg, #ef4444, #f87171)",
                    boxShadow: "0 6px 15px rgba(239, 68, 68, 0.3)",
                  }}
                >
                  <FaUserGroup size={22} color="white" />
                </span>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div
              className="card shadow-sm rounded-4 h-100 overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #ffffff, #f1f5f9)",
                position: "relative",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 20px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-15%",
                  left: "-15%",
                  width: "140px",
                  height: "140px",
                  background:
                    "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(187, 247, 208, 0.1))",
                  borderRadius: "50%",
                  filter: "blur(50px)",
                  opacity: "0.7",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: "-20%",
                  right: "-20%",
                  width: "120px",
                  height: "120px",
                  background:
                    "linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(187, 247, 208, 0.08))",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  opacity: "0.5",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "4px",
                  background: "linear-gradient(90deg, #16a34a, #22c55e)",
                  borderRadius: "4px 4px 0 0",
                }}
              ></div>
              <div
                className={`card-body d-flex justify-content-between align-items-center ${
                  isMobile ? "p-3" : "p-4"
                }`}
              >
                <div>
                  <h5
                    className="fw-bold m-0"
                    style={{ fontSize: "1.2rem", color: "#14532d" }}
                  >
                    {summary?.paid_count || 0}
                  </h5>
                  <span className="fw-medium" style={{ fontSize: "0.8rem" }}>
                    Paid Employee
                  </span>
                </div>
                <span
                  className="d-none rounded-3 d-sm-flex justify-content-center align-items-center"
                  style={{
                    padding: "0.7rem",
                    background: "linear-gradient(135deg, #16a34a, #22c55e)",
                    boxShadow: "0 4px 10px rgba(22, 163, 74, 0.3)",
                  }}
                >
                  <FaCheckCircle size={22} color="white" />
                </span>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div
              className="card shadow-sm rounded-4 h-100 overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #ffffff, #f1f5f9)",
                position: "relative",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 20px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-15%",
                  left: "-15%",
                  width: "140px",
                  height: "140px",
                  background:
                    "linear-gradient(135deg, rgba(234, 179, 8, 0.15), rgba(254, 243, 199, 0.1))",
                  borderRadius: "50%",
                  filter: "blur(50px)",
                  opacity: "0.7",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: "-20%",
                  right: "-20%",
                  width: "120px",
                  height: "120px",
                  background:
                    "linear-gradient(135deg, rgba(234, 179, 8, 0.12), rgba(254, 243, 199, 0.08))",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  opacity: "0.5",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "4px",
                  background: "linear-gradient(90deg, #eab308, #facc15)",
                  borderRadius: "4px 4px 0 0",
                }}
              ></div>
              <div
                className={`card-body d-flex justify-content-between align-items-center ${
                  isMobile ? "p-3" : "p-4"
                }`}
              >
                <div>
                  <h5
                    className="fw-bold m-0"
                    style={{ fontSize: "1.2rem", color: "#713f12" }}
                  >
                    {summary?.unpaid_count || 0}
                  </h5>
                  <span className="fw-medium" style={{ fontSize: "0.8rem" }}>
                    Unpaid Employee
                  </span>
                </div>
                <span
                  className="d-none rounded-3 d-sm-flex justify-content-center align-items-center"
                  style={{
                    padding: "0.7rem",
                    background: "linear-gradient(135deg, #eab308, #facc15)",
                    boxShadow: "0 4px 10px rgba(234, 179, 8, 0.3)",
                  }}
                >
                  <IoIosWarning size={22} color="white" />
                </span>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div
              className="card shadow-sm rounded-4 h-100 overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #ffffff, #f1f5f9)",
                position: "relative",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 20px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-15%",
                  left: "-15%",
                  width: "140px",
                  height: "140px",
                  background:
                    "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 197, 253, 0.1))",
                  borderRadius: "50%",
                  filter: "blur(50px)",
                  opacity: "0.7",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: "-20%",
                  right: "-20%",
                  width: "120px",
                  height: "120px",
                  background:
                    "linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(147, 197, 253, 0.08))",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  opacity: "0.5",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "4px",
                  background: "linear-gradient(90deg, #0ea5e9, #38bdf8)",
                  borderRadius: "4px 4px 0 0",
                }}
              ></div>
              <div
                className={`card-body d-flex justify-content-between align-items-center ${
                  isMobile ? "p-3" : "p-4"
                }`}
              >
                <div>
                  <h5
                    className="fw-bold m-0"
                    style={{ fontSize: "1.2rem", color: "#075985" }}
                  >
                    ₹ {formatToIndianNumber(summary?.total_paid_amount)}
                  </h5>
                  <span className="fw-medium" style={{ fontSize: "0.8rem" }}>
                    Total Payroll
                  </span>
                </div>
                <span
                  className="d-none rounded-3 d-sm-flex justify-content-center align-items-center"
                  style={{
                    padding: "0.7rem",
                    background: "linear-gradient(135deg, #0ea5e9, #38bdf8)",
                    boxShadow: "0 4px 10px rgba(14, 165, 233, 0.3)",
                  }}
                >
                  <SiVirustotal size={22} color="white" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Table */}
      {checkLoading ? (
        <div className="my-3 text-center">
          <Spinner className="animation-border" size="sm" />
        </div>
      ) : !isPayrollCreated ? (
        <div className="alert alert-warning text-center rounded-3 py-4">
          <div className="mb-3">
            <h5 className="fw-bold mb-1 text-darker">Payroll Not Created</h5>
            <p className="mb-0 text-dark" style={{ fontSize: "0.9rem" }}>
              No payroll has been generated for this period. Please create
              payroll to proceed.
            </p>
          </div>
          <button
            className="btn btn-primary px-4 fw-semibold"
            onClick={handleCreatePayroll}
            disabled={careatePayrollLoading}
          >
            {careatePayrollLoading && <Spinner animation="border" size="sm" />}
            Create Payroll
          </button>
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="mb-0">Employee Salaries</h4>
            <div className="d-flex gap-2 flex-wrap flex-fill flex-sm-grow-0">
              <div className="flex-fill">
                <DebounceSmallSearchInput
                  handleSearchChange={handleSearchChange}
                />
              </div>
              <div>
                <GlobalSearchableDropdown
                  options={servicecenterOption}
                  selected={selectedServiceCenter}
                  onSelect={(item) => setSelectedServiceCenter(item)}
                  placeholder="Service center"
                />
              </div>
              {selectedEmployees.length > 0 && (
                <button
                  className="btn btn-primary align-self-center"
                  onClick={handleBulkPayment}
                  style={{ padding: "0.44rem 0.7rem" }}
                >
                  Pay Selected ({selectedEmployees.length})
                </button>
              )}
            </div>
          </div>
          {loading ? (
            <div className="my-3 text-center">
              <Spinner animation="border" />
            </div>
          ) : data && data?.length > 0 ? (
            <EmployeeSalaryTable
              employees={data}
              setEmployees={setData}
              onPayEmployee={handlePayEmployee}
              selectedEmployees={selectedEmployees}
              onSelectEmployee={toggleEmployeeSelection}
              onSelectAll={handleSelectAllEmployees}
            />
          ) : (
            <div className="text-center my-3">No data found</div>
          )}
          {!loading && data?.length > 0 && (
            <div className="p-4">
              <Pagination
                currentPage={param.page}
                recordsPage={param.page_size}
                dataLength={total}
                handlePagination={handlePageChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Single Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        employee={selectedEmployee}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Bulk Payment Modal */}
      <PaymentModal
        isOpen={isBulkPaymentModalOpen}
        onClose={() => setIsBulkPaymentModalOpen(false)}
        employees={selectedEmployees}
        onPaymentSuccess={handlePaymentSuccess}
        isBulkPayment={true}
      />
    </>
  );
}
